/**
 * UI preferences — global visual configuration for MasterData.
 *
 * Surfaces on the Settings page; consumed everywhere (Pill, EntityTable,
 * page shells). Persisted to localStorage under `md.ui.prefs.v1`. Cross-tab
 * sync via the `storage` event.
 *
 * NOT covered here (kept per-page on purpose):
 *   - DensityToggle: each page already has its own `useDensity(pageKey)` so
 *     Orders' density doesn't follow Products. uiPrefs.defaultDensity is
 *     the seed value when no per-page preference exists.
 *   - Per-table state (column visibility / sort / saved views) lives in
 *     `prefs.tables[tableId]` so it can be reset table-by-table.
 *
 * NOTE: this file deliberately uses `React.createElement` instead of JSX so
 * it can stay a `.ts` (no .tsx rename needed during the cohesion sweep).
 */
import {
  createContext, createElement, type ReactNode,
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react'
import { normalizeColumnOrder } from '../../lib/tableLayout'
import { COLUMN_RENAMES_V11, LISTINGS_TABLE_ID, renameStoredColumns } from './storedLayoutMigrations'
import type { ListingStatus } from './api'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type PillStyle = 'flat' | 'leading-dot'
export type ZebraMode = 'auto' | 'always' | 'never'
export type Density = 'compact' | 'comfy'
export type HeatmapMode = 'both' | 'diverging-only' | 'sequential-only' | 'off'
export type CsvFormat = 'download' | 'clipboard' | 'both'

/** Per-table state — column visibility, order, saved views, sort, density. */
export interface SavedView {
  /** url slug like "critical-drop" */
  slug: string
  /** human label like "Critical + Drop" */
  label: string
  /** Created at ISO timestamp. */
  created_at: string
  /** Filter chips snapshot. */
  filters?: Array<{ id: string; value: unknown }>
  /** Sort order snapshot. */
  sort?: Array<{ id: string; desc: boolean }>
  /** Visible column ids. */
  visibility?: Record<string, boolean>
  /** Column order. */
  columnOrder?: string[]
  /** Density. */
  density?: Density
  /** Global search text. */
  search?: string
}

export interface TablePrefs {
  /** Per-column visibility. Missing keys default to visible. */
  visibility?: Record<string, boolean>
  /** Column order (list of column ids). */
  columnOrder?: string[]
  /**
   * Persisted column widths (Tables Upgrade Phase 1, task 1) — px, keyed by
   * column id, mirroring TanStack's `ColumnSizingState` shape 1:1 so it can be
   * dropped straight into `useReactTable({ state: { columnSizing } })`. Only
   * holds columns the user has actually dragged-resized; columns absent here
   * fall back to the ColDef/format-seeded width as before. Shape matches the
   * main-app table's `columnWidths` (`lib/tableLayout.tsx`) on purpose so the
   * Phase 2 store-unification merge is field-for-field, not a translation.
   */
  columnWidths?: Record<string, number>
  /**
   * PR 9a (L5b) -- per-column width versions: column id -> the `ColDef.widthRev`
   * current when that column's width was saved. A width stamped under an older
   * rev than the column's current one reads as absent (for that column only);
   * missing = 1. Additive, so `STORED_LAYOUT_VERSION` is not bumped.
   */
  columnWidthRevs?: Record<string, number>
  /**
   * Column pin/freeze (Tables Upgrade Phase 1, task 2) — ordered list of
   * column ids frozen to the left edge via the column menu's "Freeze up to
   * here" action, persisted so a reload keeps the freeze. Does NOT include
   * columns a page pins statically via `ColDef.pinned: 'left'` — those are
   * re-derived from the page's column defs on every load, not persisted here
   * (so removing the `pinned` flag from a ColDef cleanly un-pins it without a
   * stale prefs entry). Order matters: left-to-right visual stacking order,
   * not click order.
   */
  pinnedColumnIds?: string[]
  /**
   * User-pinned default column order — set via "Save current as default" in
   * the column visibility menu. When set, this becomes the order that
   * "Reset" restores to (instead of the page's hardcoded `cols.map(colId)`).
   * Persists across uiPrefs schema migrations: future `_version` bumps that
   * clear `columnOrder` MUST preserve `defaultColumnOrder`.
   */
  defaultColumnOrder?: string[]
  /** Same as `defaultColumnOrder` but for column visibility. */
  defaultVisibility?: Record<string, boolean>
  /**
   * Per-column heatmap override (2026-06-03 operator request: "add a button
   * to color code so I can add it where I want"). Keys are column ids
   * (colId(c) — same shape as `visibility`). Values:
   *   - `true`  → force heat ON, even if the ColDef has no static `heat`.
   *               Uses scale='sequential' as the fallback ramp.
   *   - `false` → force heat OFF, even if the ColDef defines `heat`.
   *   - missing → use the ColDef's static `heat` (default).
   * Persists across reloads alongside visibility + column order.
   */
  heatOverrides?: Record<string, boolean>
  /**
   * Whether this table's per-column filter row is expanded (F1 task 3 —
   * "calm headers"). Unset → collapsed, unless the table restores with a
   * filter already applied. Per-table because someone who lives in the
   * Timeline's filter row shouldn't have Products' headers cluttered too.
   */
  showColumnFilters?: boolean
  /** Saved views for this table. */
  views?: SavedView[]
  /** Orders page only: which view is active. */
  view?: 'grouped' | 'table'
  /** RestockHub tab badges only: sum of send qty vs row count. */
  bubbleMode?: 'sum' | 'count'
  /** PR-14 — Sellerboard-style grouping level (GroupingPicker), persisted
   *  per-table. Unset -> 'uid' (no grouping). */
  groupBy?: string
  /** Restock sub-tab, every level-1 tab: hide rows whose listing on that
   *  tab's marketplace is not 'active'. Unset → true (owner default, #84
   *  2026-09-09). Stored under ONE key — RestockHub.tsx's
   *  RESTOCK_ACTIVE_ONLY_KEY — so the switch is the same on every restock
   *  tab. */
  activeOnly?: boolean
}

/**
 * Saved default window for the Products / Marketplace-performance tile row.
 *
 * Stores the RELATIVE tile selector — the tile group id + the active card id
 * (+ compare mode) — NOT resolved dates, so a saved "Today"/"Last month"
 * re-resolves against the current day each load (tile windows are computed from
 * `now` at render). For group='custom', activeId='custom' and the concrete
 * from/to already persist in the shared 'adbetter.dateRange.pnl' storage, so
 * custom defaults work with zero extra storage here.
 *
 * Typed structurally with plain strings (not the hub's TileGroupId/CompareMode
 * literals) to keep this lib dependency-free — the consumer narrows on read.
 */
export interface ProductsDefaultWindow {
  /** TileGroupId (e.g. 'days_ago', 'months', 'custom'). */
  group: string
  /** Card id within the group (e.g. 'today', 'mtd', 'm1', 'custom'). */
  activeId: string
  /** CompareMode (e.g. 'none', 'previous', 'last_year'). Optional. */
  compare?: string
}

/** Thumbnail sizes offered on the Timeline, in px. 0 = column hidden. */
export const ORDERS_IMAGE_SIZES = [0, 24, 40, 72, 112] as const
export type OrdersImageSize = (typeof ORDERS_IMAGE_SIZES)[number]

/** Human label for each size, for the settings control. */
export const ORDERS_IMAGE_SIZE_LABELS: Record<OrdersImageSize, string> = {
  0: 'Off', 24: 'S', 40: 'M', 72: 'L', 112: 'XL',
}

/**
 * How tall a row must be to hold `image` px of picture at `base` px of row.
 *
 * Row height is otherwise fixed per density and written straight onto the
 * <tr>, so a 112px thumbnail in a 32px row is simply cropped. It also has to
 * be the number fed to the row VIRTUALISER: that windows rows from a fixed
 * height seed, and seeding it with the un-grown height mis-positions every
 * row below the fold as you scroll.
 */
export function rowHeightFor(base: number, image: number): number {
  return image > 0 ? Math.max(base, image + 8) : base
}

export interface UIPrefs {
  /** Pill visual: flat (current legacy) or leading-dot (Sellerboard). */
  pillStyle: PillStyle
  /** Tabular numerics on right-aligned cells. */
  tabularNums: boolean
  /** Zebra rows on table rows. */
  zebra: ZebraMode
  /** Default density when a page hasn't been customized. */
  defaultDensity: Density
  /** Heatmap mode — which kinds of `heat` to render. */
  heatmapMode: HeatmapMode
  /** Heatmap intensity 0-100 (% opacity applied to ramp color). */
  heatmapIntensity: number
  /** CSV export default action — download / clipboard / both. */
  csvFormat: CsvFormat
  /** How large the product thumbnail is on a Timeline row, in px. 0 hides
   *  the column. The ROW grows to fit it (see `rowHeightFor`) — a picture
   *  taller than the row would otherwise just be clipped. */
  ordersImageSize: OrdersImageSize
  /** Per-table preferences keyed by `tableId`. */
  tables: Record<string, TablePrefs>
  /** Saved default window for the Products / Marketplace-performance tile row.
   *  Unset → the tiles fall back to the 'Today' card. Optional + merged safely
   *  by loadPrefs / the storage handler, so no _version bump is needed. */
  productsDefaultWindow?: ProductsDefaultWindow
  /** Schema version for forward-migration. */
  _version: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_PREFS: UIPrefs = {
  pillStyle: 'leading-dot',
  tabularNums: true,
  zebra: 'auto',
  defaultDensity: 'compact',
  heatmapMode: 'both',
  heatmapIntensity: 12,
  csvFormat: 'both',
  ordersImageSize: 40,
  tables: {},
  _version: 11,
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared layout keys
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ONE persisted column layout for every table on the Timeline (Orders) tab.
 *
 * THE BUG THIS FIXES (cost-derivation program F1, task 1 — owner complaint
 * "tables are not customizable, columns cannot be rearranged", 2026-08-05):
 * the shared EntityTable has had direct on-header drag-reorder since
 * 2026-06-03 (`EntityTable.tsx` `reorderViaDrag`), so the columns *could*
 * always be rearranged. What made it FEEL like they couldn't is that the
 * Timeline renders EIGHT separate table instances — one per status section
 * plus the flat view — each persisting its own layout under its own
 * `tableId`. Reordering a column on the Received section left the other six
 * sections and the flat view untouched, so the change appeared not to stick.
 *
 * The fix is one persisted LAYOUT shared by N table INSTANCES: every Orders
 * table keeps its own `tableId` (saved views, sort and session state are
 * still legitimately per-section) but points its column layout at this key.
 */
export const ORDERS_LAYOUT_ID = 'orders'

/**
 * The eight per-instance keys `ORDERS_LAYOUT_ID` replaces, newest-shape
 * first. Read-only fallbacks kept for one release so an employee who had
 * customized the Timeline before the shared key existed does not lose it;
 * the localStorage copies are folded forward by the `_version` 6 → 7
 * migration below, and the SERVER copies by `resolveLayoutAcrossKeys`
 * (`tableLayout.tsx`), which tries these in order after the shared key.
 */
export const ORDERS_LEGACY_LAYOUT_IDS: readonly string[] = [
  'orders.table',
  'orders.section.v2.Received',
  'orders.section.v2.Customs',
  'orders.section.v2.In Transit',
  'orders.section.v2.Scheduled',
  'orders.section.v2.Ready',
  'orders.section.v2.Production',
  'orders.section.v2.Ordered',
]

/**
 * The Shipments tab's shared column layout, added when that tab moved from
 * one flat table to four status sections (2026-09-10). Same reasoning as
 * `ORDERS_LAYOUT_ID`: the sections are one table split by status, so they
 * must share ONE column order / width / visibility set or reordering becomes
 * a per-section chore.
 *
 * No legacy-key list is needed: the flat table rendered with
 * `tableId="shipments"` and no `layoutId`, and EntityTable resolves
 * `layoutKey = layoutId ?? tableId`, so this key IS the one already on disk.
 */
export const SHIPMENTS_LAYOUT_ID = 'shipments'

/**
 * How much column customization a stored `TablePrefs` actually carries.
 *
 * Used to pick a winner when several per-instance keys are being folded into
 * one shared key and more than one of them has a layout. Saved views count
 * heaviest (10× — a named view is a deliberate act an employee would notice
 * losing), then explicit column order and visibility choices; widths and pins
 * count once each. Pure and exported so `uiPrefs.test.ts` can pin the
 * ordering down rather than trusting a hand-check of the migration.
 */
export function layoutRichness(t: TablePrefs | undefined): number {
  if (!t) return -1
  return (
    Object.keys(t.visibility ?? {}).length
    + (t.columnOrder?.length ?? 0)
    + Object.keys(t.columnWidths ?? {}).length
    + (t.pinnedColumnIds?.length ?? 0)
    + (t.views?.length ?? 0) * 10
  )
}

/**
 * Pick the most-customized of several candidate layouts (see
 * `layoutRichness`). Ties break toward the EARLIER candidate, so callers
 * should pass their preferred key first. Returns undefined when no candidate
 * carries anything worth keeping — the caller then leaves the shared key
 * unset so the page's own column defaults apply, which is the right outcome
 * for a user who never customized. Pure; exported for tests.
 */
export function pickRichestLayout(
  candidates: ReadonlyArray<TablePrefs | undefined>,
): TablePrefs | undefined {
  let best: TablePrefs | undefined
  let bestScore = 0
  for (const c of candidates) {
    const score = layoutRichness(c)
    if (score > bestScore) { bestScore = score; best = c }
  }
  return best
}

// ─────────────────────────────────────────────────────────────────────────────
// Storage
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'md.ui.prefs.v1'

function loadPrefs(): UIPrefs {
  if (typeof window === 'undefined') return { ...DEFAULT_PREFS }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_PREFS }
    const parsed = JSON.parse(raw) as Partial<UIPrefs>
    const merged: UIPrefs = {
      ...DEFAULT_PREFS,
      ...parsed,
      tables: { ...DEFAULT_PREFS.tables, ...(parsed.tables ?? {}) },
    }
    // _version=1 → 2 migration (2026-05-27): the single shared `restock_hub`
    // tableId is being replaced by per-tab ids `restock_hub.<marketplace>`.
    // Copy any v1 prefs into the new "Supplier Reorder" tab id so the user
    // doesn't lose their column setup on first load post-upgrade.
    if ((parsed._version ?? 1) < 2) {
      const oldHub = merged.tables['restock_hub']
      if (oldHub && !merged.tables['restock_hub.supplier_reorder']) {
        merged.tables['restock_hub.supplier_reorder'] = oldHub
      }
      delete merged.tables['restock_hub']
      merged._version = 2
    }
    // _version=2 → 3 migration (2026-05-27): column-order reorder for the
    // checkbox/drive/kebab-leftmost layout means saved orders are stale.
    // Drop columnOrder on every restock_hub.* and payments table so the
    // default order (from cols definition) takes effect on next load.
    // Column visibility + saved views + bubbleMode are preserved.
    if ((parsed._version ?? 1) < 3) {
      for (const k of Object.keys(merged.tables)) {
        if (k.startsWith('restock_hub.') || k === 'payments') {
          const t = merged.tables[k]
          if (t && t.columnOrder) {
            const { columnOrder, ...rest } = t
            merged.tables[k] = rest
          }
        }
      }
      merged._version = 3
    }
    // _version=3 → 4 migration (2026-05-28): per-marketplace tabs on
    // RestockHub now render MPL-Restock-shape rows + cols, with the
    // checkbox/drive/kebab cluster on the left. Saved column orders +
    // visibility from the old SR-shape don't translate. Clear them on
    // every restock_hub.<marketplace> table key (NOT supplier_reorder,
    // which retains its SR-shape cols + order). User's defaultColumnOrder
    // and defaultVisibility are preserved.
    if ((parsed._version ?? 1) < 4) {
      for (const k of Object.keys(merged.tables)) {
        if (k.startsWith('restock_hub.') && k !== 'restock_hub.supplier_reorder') {
          const t = merged.tables[k]
          if (t) {
            const { columnOrder, visibility, ...rest } = t
            merged.tables[k] = rest
          }
        }
      }
      merged._version = 4
    }
    // _version=4 → 5 migration (2026-06-02): default column order changed in
    // buildCols (RestockHub.tsx) and buildMplCols (restockMplCols.tsx) so the
    // decision-driving columns (DoS, Trend, AI Order, Reorder Qty/$) cluster
    // immediately right of identity, with Supplier moved to the metadata tail.
    // Drop persisted columnOrder + defaultColumnOrder on every restock_hub.*
    // table (including supplier_reorder, which DID reflow) so the new code
    // default takes effect on next load. Visibility, Saved Views (views[]),
    // and bubbleMode are preserved — column ids didn't change, just their
    // order, so visibility/view snapshots remain valid.
    if ((parsed._version ?? 1) < 5) {
      for (const k of Object.keys(merged.tables)) {
        if (k.startsWith('restock_hub.')) {
          const t = merged.tables[k]
          if (t) {
            const { columnOrder, defaultColumnOrder, ...rest } = t
            merged.tables[k] = rest
          }
        }
      }
      merged._version = 5
    }
    // _version=5 → 6 migration (2026-06-03): per-marketplace RestockHub tabs
    // now SHARE one tableId ('restock_hub.marketplace_view') so a column
    // hide/show or reorder on Amazon US also takes effect on Amazon CA /
    // Walmart / etc. Pre-fix each MP had its own id ('restock_hub.amazon_us',
    // 'restock_hub.amazon_ca', …). Copy any existing per-MP setup into the
    // shared id (prefer the largest by visibility-entry count so we keep the
    // most-customized version), then delete the orphan per-MP keys.
    // 'restock_hub.supplier_reorder' is preserved — different col shape.
    if ((parsed._version ?? 1) < 6) {
      const perMpKeys = Object.keys(merged.tables).filter(
        (k) => k.startsWith('restock_hub.') && k !== 'restock_hub.supplier_reorder'
                                            && k !== 'restock_hub.marketplace_view'
      )
      if (perMpKeys.length > 0 && !merged.tables['restock_hub.marketplace_view']) {
        let best: string | null = null
        let bestScore = -1
        for (const k of perMpKeys) {
          const t = merged.tables[k]
          if (!t) continue
          const score = Object.keys(t.visibility ?? {}).length
                      + (t.columnOrder?.length ?? 0)
                      + (t.views?.length ?? 0) * 10
          if (score > bestScore) { bestScore = score; best = k }
        }
        if (best) {
          merged.tables['restock_hub.marketplace_view'] = merged.tables[best]!
        }
      }
      for (const k of perMpKeys) delete merged.tables[k]
      merged._version = 6
    }
    // _version=6 → 7 migration (2026-08-05, cost-derivation program F1 task
    // 1): the Timeline's eight table instances stop each owning a column
    // layout and share ONE — see `ORDERS_LAYOUT_ID`'s doc for why that is the
    // actual fix for "the columns cannot be rearranged". Fold the richest
    // existing per-instance layout forward into the shared key so nobody's
    // setup resets on upgrade (`pickRichestLayout`; the flat view is passed
    // first so it wins a tie — it is the only one of the eight that shows
    // every status at once, so its layout is the most likely to be the one
    // the user actually curated).
    //
    // The old keys are LEFT IN PLACE, not deleted: `EntityTable`'s
    // `legacyLayoutIds` still reads them as a fallback for one release, and
    // keeping them means a rollback of this build lands the user back on
    // exactly the layout they had. They cost a few hundred bytes of
    // localStorage until the next schema bump sweeps them.
    if ((parsed._version ?? 1) < 7) {
      // NOTE `tables['orders']` very likely already EXISTS — it is where the
      // Timeline's grouped-vs-flat `view` toggle has always been stored. So
      // the guard is "does the shared entry already carry a LAYOUT", not
      // "does the entry exist"; testing existence would silently skip the
      // fold-forward for every current user and reset their columns.
      const shared = merged.tables[ORDERS_LAYOUT_ID]
      if (layoutRichness(shared) <= 0) {
        const richest = pickRichestLayout(
          ORDERS_LEGACY_LAYOUT_IDS.map((k) => merged.tables[k]),
        )
        if (richest) {
          merged.tables[ORDERS_LAYOUT_ID] = {
            ...(shared ?? {}),
            visibility: richest.visibility,
            columnOrder: richest.columnOrder,
            columnWidths: richest.columnWidths,
            pinnedColumnIds: richest.pinnedColumnIds,
            defaultColumnOrder: richest.defaultColumnOrder,
            defaultVisibility: richest.defaultVisibility,
          }
        }
      }
      merged._version = 7
    }
    // _version=7 -> 8 migration (2026-09-02): the Amazon Search Query
    // Performance columns on the SQP tables shipped `defaultHidden: true`,
    // so the owner's "the table is missing most of the SQP datapoints"
    // stayed true even after they were added -- they existed but were off.
    // Flipping `defaultHidden` alone does NOT reach anyone who has already
    // opened the tab: EntityTable's late-arriving-columns effect seeds
    // `visibility[id] = false` for every defaultHidden column and that map
    // is then persisted, so the initializer's `!(id in persisted)` guard
    // sees an explicit entry and keeps honouring it forever. Delete just
    // those keys (NOT the whole map) so the new default takes effect while
    // every other deliberate show/hide on the table survives.
    if ((parsed._version ?? 1) < 8) {
      const AMAZON_SQP_COLS = [
        'total_impressions', 'total_clicks', 'total_click_rate', 'brand_clicks',
        'total_cart_adds', 'total_cart_add_rate', 'brand_cart_adds',
        'cart_add_share', 'total_purchases', 'total_purchase_rate',
        'brand_purchases',
      ]
      for (const k of Object.keys(merged.tables)) {
        if (!k.startsWith('ba.sqp')) continue
        const t = merged.tables[k]
        if (!t?.visibility) continue
        const vis = { ...t.visibility }
        let touched = false
        for (const col of AMAZON_SQP_COLS) {
          if (col in vis) { delete vis[col]; touched = true }
        }
        if (touched) merged.tables[k] = { ...t, visibility: vis }
      }
      merged._version = 8
    }
    // _version=8 -> 9 migration (2026-09-10, Timeline Grouped-view redesign):
    // the packing list (cartons, case pack, the four carton dimensions,
    // carton CBM, line CBM) shipped `defaultHidden: !isTable`, so the whole
    // block was invisible in Grouped view -- the view everyone works in. Same
    // shape of problem as the SQP block above, and the same fix: flipping the
    // default alone never reaches a user who has already opened the tab,
    // because their persisted `visibility` map carries an explicit `false`
    // that the initializer keeps honouring. Delete those keys only, on the
    // Timeline's layout keys only, so every other deliberate show/hide lives.
    if ((parsed._version ?? 1) < 9) {
      const TIMELINE_PACKING_COLS = [
        'cartons', 'units_per_carton', 'carton_length_cm', 'carton_width_cm',
        'carton_height_cm', 'carton_weight_kg', 'carton_cbm', 'line_cbm',
      ]
      const TIMELINE_KEYS = [ORDERS_LAYOUT_ID, ...ORDERS_LEGACY_LAYOUT_IDS]
      for (const k of TIMELINE_KEYS) {
        const t = merged.tables[k]
        if (!t?.visibility) continue
        const vis = { ...t.visibility }
        let touched = false
        for (const col of TIMELINE_PACKING_COLS) {
          if (col in vis) { delete vis[col]; touched = true }
        }
        if (touched) merged.tables[k] = { ...t, visibility: vis }
      }
      merged._version = 9
    }

    // 9 -> 10 (2026-09-15). The Timeline's columns were reordered and its
    // frozen prefix changed: identity now reads PO# -> UID -> Product (how a
    // line is actually referred to), the row menu moved next to the checkbox
    // instead of being stranded fifth by the old page-level pins, and the
    // thumbnail moved in beside the product name.
    //
    // NONE of that reaches an existing user on its own. `mergeDeclaredColumnOrder`
    // only splices in ids it has never seen; it never re-orders ids the saved
    // array already contains. So the saved order has to go. Visibility, widths
    // and pins survive -- they are choices, the order was just a default.
    //
    // ASIN also becomes default-hidden here. That is a `defaultHidden` flip,
    // which the persisted-`false` ratchet would likewise ignore, so its key is
    // deleted the same way the v8->v9 packing migration did.
    if ((parsed._version ?? 1) < 10) {
      const TIMELINE_KEYS = [ORDERS_LAYOUT_ID, ...ORDERS_LEGACY_LAYOUT_IDS]
      for (const k of TIMELINE_KEYS) {
        const t = merged.tables[k]
        if (!t) continue
        const next: TablePrefs = { ...t }
        if (next.columnOrder) delete next.columnOrder
        if (next.visibility && 'asin' in next.visibility) {
          const vis = { ...next.visibility }
          delete vis.asin
          next.visibility = vis
        }
        merged.tables[k] = next
      }
      merged._version = 10
    }
    // _version=10 -> 11 migration (2026-09-23, #114): "purchase rate should be
    // CVR. Purchases / Clicks". On the SQP tables `cvr` (market purchases /
    // market clicks) becomes visible by default and Amazon's per-SEARCH
    // `total_purchase_rate` becomes hidden-by-default. Same shape as v8: drop
    // only those two keys so the new defaults apply and every other choice on
    // the table survives. Mirrored in storedLayoutMigrations.ts (server layer).
    if ((parsed._version ?? 1) < 11) {
      for (const k of Object.keys(merged.tables)) {
        if (!k.startsWith('ba.sqp')) continue
        const t = merged.tables[k]
        if (!t?.visibility) continue
        const vis = { ...t.visibility }
        let touched = false
        for (const col of ['cvr', 'total_purchase_rate']) {
          if (col in vis) { delete vis[col]; touched = true }
        }
        if (touched) merged.tables[k] = { ...t, visibility: vis }
      }
      // Listings' SCP rate column changed id with its meaning
      // (__scp_purch_rate -> __scp_cvr); carry the saved order/width/visibility.
      const lt = merged.tables[LISTINGS_TABLE_ID]
      if (lt) merged.tables[LISTINGS_TABLE_ID] = renameStoredColumns(lt, COLUMN_RENAMES_V11)
      merged._version = 11
    }
    return merged
  } catch {
    return { ...DEFAULT_PREFS }
  }
}

function savePrefs(p: UIPrefs): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p))
  } catch {
    // localStorage unavailable / quota — best-effort, ignore.
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// React context + provider
// ─────────────────────────────────────────────────────────────────────────────

interface UIPrefsApi {
  prefs: UIPrefs
  setPrefs: (next: UIPrefs | ((cur: UIPrefs) => UIPrefs)) => void
  /** Convenience: shallow merge a partial. */
  patch: (partial: Partial<UIPrefs>) => void
  /** Get table prefs for an id; never returns undefined. */
  tablePrefs: (tableId: string) => TablePrefs
  /** Set table prefs for a specific id. */
  setTablePrefs: (tableId: string, next: TablePrefs | ((cur: TablePrefs) => TablePrefs)) => void
  /** Reset everything to DEFAULT_PREFS. */
  reset: () => void
}

/**
 * The prefs reducers, extracted from the provider so the ONE rule they exist
 * to enforce is a named function a test can hold:
 *
 *   **A write that changes nothing must not produce a new state value.**
 *
 * Every layout persist effect in EntityTable is written as
 *   `setTablePrefs(key, (cur) => sameAsStored ? cur : { ...cur, x })`
 * and each carries a comment claiming the `return cur` branch "stops the
 * on-mount feedback loop". It never did. The old setter built
 * `{ ...cur, tables: { ...cur.tables, [tableId]: nextT } }` unconditionally,
 * so an updater that DECLINED to write still replaced `prefs`, which replaced
 * the context value (`api` is memoised on `prefs`), which re-rendered every
 * `useUIPrefs()` consumer, which re-ran their persist effects, which called
 * back in here. The guards were real; the setter discarded them.
 *
 * Measured on /inventory/marketplaces (Restock, Amazon) before this fix:
 * **23 localStorage writes per second** of byte-identical prefs, React error
 * #185 ("Maximum update depth exceeded") in the console, the Restock column
 * order visibly oscillating, and a main thread so starved that clicking a
 * marketplace tab changed the URL but never repainted -- the "tabs stick"
 * report. One unconditional object spread, two user-visible bugs.
 *
 * Why the loop closed on Restock and not on, say, /placements: RestockHub's
 * `mplCols` memo lists `allRows` and `draftCart` in its deps, so a re-render
 * yields a NEW `cols` array; EntityTable's `staticPinnedIds` is
 * `useMemo(() => new Set(...), [cols])` and is a dep of the column-pinning
 * persist effect. New cols -> new Set -> effect re-fires -> setTablePrefs ->
 * new prefs -> re-render. Tables whose `cols` identity is stable never closed
 * the cycle, which is why this read as "the Restock table is broken" rather
 * than "the prefs setter is broken".
 */
export function applyTablePrefs(
  cur: UIPrefs,
  tableId: string,
  next: TablePrefs | ((curT: TablePrefs) => TablePrefs),
): UIPrefs {
  // EMPTY_TABLE_PREFS, not a fresh `{}`: an updater that returns its input
  // must be recognised as a no-op on the FIRST call too, before this table
  // has any stored prefs -- which is exactly when the mount-time storm ran.
  const curT = cur.tables[tableId] ?? EMPTY_TABLE_PREFS
  const nextT = typeof next === 'function' ? next(curT) : next
  if (nextT === curT) return cur
  return { ...cur, tables: { ...cur.tables, [tableId]: nextT } }
}

/**
 * Same rule for the shallow-merge convenience setter: patching fields to the
 * values they already hold is not a change. Reference equality per key (not
 * deep) -- enough to stop an effect that patches a scalar or a memoised
 * object every render, which is the loop shape that actually occurs, without
 * paying a deep compare on every keystroke-driven patch.
 */
export function applyPatch(cur: UIPrefs, partial: Partial<UIPrefs>): UIPrefs {
  const keys = Object.keys(partial) as (keyof UIPrefs)[]
  if (keys.every((k) => cur[k] === partial[k])) return cur
  return { ...cur, ...partial }
}

const Ctx = createContext<UIPrefsApi | null>(null)

export function UIPrefsProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefsState] = useState<UIPrefs>(() => loadPrefs())

  // The state object most recently adopted FROM ANOTHER TAB. It is already in
  // localStorage -- the other tab put it there -- so saving it again is an echo.
  //
  // Echoes are not harmless. Two tabs normally converge only because identical
  // text does not fire a `storage` event. Any tab that serialises the same prefs
  // with different key order breaks that: a tab still running the PREVIOUS build
  // after a deploy that adds a pref key rebuilds state as {...OLD_DEFAULTS,
  // ...parsed}, which orders keys differently, so every save re-fires the event
  // in the other tab and they loop forever at render speed. Reproduced
  // 2026-09-16: the Timeline froze solid, and a view change made in one tab can
  // be overwritten by the other tab's in-flight echo.
  //
  // Identity, not a boolean flag: a local edit made in the same batch produces a
  // NEW object and is still saved.
  const adoptedRef = useRef<UIPrefs | null>(null)

  useEffect(() => {
    if (prefs === adoptedRef.current) return
    savePrefs(prefs)
  }, [prefs])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      if (e.newValue == null) {
        const next = { ...DEFAULT_PREFS }
        adoptedRef.current = next
        setPrefsState(next)
        return
      }
      try {
        const parsed = JSON.parse(e.newValue) as Partial<UIPrefs>
        const next: UIPrefs = {
          ...DEFAULT_PREFS,
          ...parsed,
          tables: { ...DEFAULT_PREFS.tables, ...(parsed.tables ?? {}) },
        }
        adoptedRef.current = next
        setPrefsState(next)
      } catch {
        // ignore — keep current state
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const setPrefs = useCallback<UIPrefsApi['setPrefs']>((next) => {
    setPrefsState((cur) => (typeof next === 'function' ? next(cur) : next))
  }, [])

  const patch = useCallback<UIPrefsApi['patch']>((partial) => {
    setPrefsState((cur) => applyPatch(cur, partial))
  }, [])

  // EMPTY_TABLE_PREFS lives at MODULE scope (declared at the bottom of the
  // file). Putting it inside the provider body would create a new `{}` on
  // every provider render — defeating the "stable singleton" intent.
  const tablePrefs = useCallback<UIPrefsApi['tablePrefs']>((tableId) => {
    return prefs.tables[tableId] ?? EMPTY_TABLE_PREFS
  }, [prefs.tables])

  const setTablePrefs = useCallback<UIPrefsApi['setTablePrefs']>((tableId, next) => {
    setPrefsState((cur) => applyTablePrefs(cur, tableId, next))
  }, [])

  const reset = useCallback(() => {
    setPrefsState({ ...DEFAULT_PREFS })
  }, [])

  const api = useMemo<UIPrefsApi>(() => ({
    prefs, setPrefs, patch, tablePrefs, setTablePrefs, reset,
  }), [prefs, setPrefs, patch, tablePrefs, setTablePrefs, reset])

  return createElement(Ctx.Provider, { value: api }, children)
}

export function useUIPrefs(): UIPrefsApi {
  const v = useContext(Ctx)
  if (!v) {
    return {
      prefs: DEFAULT_PREFS,
      setPrefs: () => {},
      patch: () => {},
      tablePrefs: () => EMPTY_TABLE_PREFS,
      setTablePrefs: () => {},
      reset: () => {},
    }
  }
  return v
}

// Module-scope stable singleton — `useUIPrefs().tablePrefs(id)` returns this
// reference when there's nothing persisted for `id`. Hoisted out of the
// provider body so it doesn't re-allocate every render.
const EMPTY_TABLE_PREFS: TablePrefs = Object.freeze({}) as TablePrefs

// ─────────────────────────────────────────────────────────────────────────────
// Helpers — heatmap & cell tint
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a 0..1 normalized value + heatmap stops into an `rgba()` background
 * applying user-selected intensity. Returns "" when heatmap is off.
 */
export function heatTint(
  norm: number | null | undefined,
  ramp: { stops: readonly string[] },
  intensityPct: number,
): string {
  if (norm == null || Number.isNaN(norm)) return ''
  if (intensityPct <= 0) return ''
  const clamped = Math.max(0, Math.min(1, norm))
  const idx = Math.min(
    ramp.stops.length - 1,
    Math.floor(clamped * (ramp.stops.length - 0.0001)),
  )
  const hex = ramp.stops[idx]
  const alpha = Math.min(1, Math.max(0, intensityPct / 100))
  // hex → rgb
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Vertical cell padding of a data row and of a header cell at a density. At
 *  13px type, compact puts a row near 27px and comfortable near 35px. The
 *  main-app EntityTable reads this through the density preference (PR 12, D7);
 *  named here so density stays one decision. */
export function rowPadding(density: Density): { row: string; head: string } {
  return density === 'comfy' ? { row: 'py-2', head: 'py-2.5' } : { row: 'py-1', head: 'py-1.5' }
}

/** The Export menu's items for the CSV preference: download only, copy only,
 *  or both. */
export function exportFormatsFor(csv: CsvFormat): 'csv' | 'tsv' | 'both' {
  return csv === 'download' ? 'csv' : csv === 'clipboard' ? 'tsv' : 'both'
}

/**
 * Resolve whether a row should get zebra-striping at this density.
 * Auto = comfy-only. Always = always. Never = never.
 */
export function resolveZebra(mode: ZebraMode, density: Density, rowIndex: number): boolean {
  if (rowIndex % 2 === 0) return false
  if (mode === 'never') return false
  if (mode === 'always') return true
  // auto
  return density === 'comfy'
}

// ─────────────────────────────────────────────────────────────────────────────
// Restock Hub — per-marketplace matrix layout (#79) & Active-listings-only (#84)
//
// Pure, framework-free helpers so RestockHub.tsx's Batch 8 changes are
// testable without mounting the page. They live here (not in a new
// restockHub/*.ts module) because RestockHub.tsx exports React components
// (VelocityAndAIExplainer, PerMarketplaceMatrix, the default page) — mixing
// plain function exports in alongside those trips
// react-refresh/only-export-components. This module already exports plain
// functions (applyTablePrefs, heatTint, resolveZebra, …) with none of that
// warning, so it's the natural home.
// ─────────────────────────────────────────────────────────────────────────────

// ─── #79 — per-marketplace matrix layout (reorder + hide) ──────────────────
// ONE key for every product's drawer — that is what makes a reorder/hide on
// one product apply to every product, exactly like EntityTable's `layoutId`
// (EntityTable.tsx:378/:830). Stored via useUIPrefs().tablePrefs, same store
// RestockHub.tsx's 'restock_hub.group_by' already uses.
export const MATRIX_PREFS_KEY = 'restock_hub.per_mp_matrix'

/** The matrix is TRANSPOSED — metrics are ROWS — so `columnOrder` here orders
 *  metric rows and `visibility` hides them. The field names are kept as-is so
 *  the shape drops straight into the existing TablePrefs store and into the
 *  reused ColumnVisibilityMenu unchanged. */
export type MatrixLayout = Pick<TablePrefs, 'visibility' | 'columnOrder'>

/** Apply a saved layout to a list of id-carrying rows: drop ids that no
 *  longer exist, append ids added since the layout was saved (so a metric
 *  added later shows up, never silently disappears), then drop rows the
 *  layout marked hidden. Returns the SAME array reference when the layout is
 *  empty, so a memoised caller doesn't re-render for nothing. */
export function applyMatrixLayout<T extends { id: string }>(rows: T[], layout: MatrixLayout): T[] {
  if (!layout.visibility && !layout.columnOrder) return rows
  const order = normalizeColumnOrder(layout.columnOrder ?? [], rows.map((r) => ({ key: r.id })))
  const byId = new Map(rows.map((r) => [r.id, r]))
  const out: T[] = []
  for (const id of order) {
    const r = byId.get(id)
    if (!r) continue
    if (layout.visibility?.[id] === false) continue
    out.push(r)
  }
  return out
}

/** Stable sort of marketplaces by a metric's numeric value, descending, with
 *  null/undefined pushed to the end. `valueOf: null` (no metric selected)
 *  returns the SAME array reference and never mutates its input — the fix
 *  for the latent bug where sort used to key on a metric's LABEL, which
 *  changes text when a month is cross-filtered, silently resetting an
 *  active sort. */
export function sortMarketplacesByMetric<M>(
  mps: M[],
  valueOf: ((m: M) => number | null | undefined) | null,
): M[] {
  if (!valueOf) return mps
  return [...mps].sort((a, b) => {
    const va = valueOf(a)
    const vb = valueOf(b)
    if (va == null && vb == null) return 0
    if (va == null) return 1
    if (vb == null) return -1
    return vb - va
  })
}

// ─── #84 — every restock tab defaults to "Active listings only" ────────────
// ONE key shared by every restock tab (SR + all per-MP tabs) so the switch
// reads the same everywhere.
export const RESTOCK_ACTIVE_ONLY_KEY = 'restock_hub.active_only'
export const ACTIVE_ONLY_DEFAULT = true

/** Per-marketplace (MPL) row shape: `listing_status` IS a real top-level
 *  field. Strict on 'active' — in_review, inactive AND unknown (no GoFlow
 *  listing row at all) are all hidden. On a per-marketplace send-to-FBA tab
 *  a product with no listing on that marketplace has nowhere to be sent. */
export function isActiveMplRow(r: { listing_status?: ListingStatus | null }): boolean {
  return r.listing_status === 'active'
}

/** Supplier-Reorder (pooled) row shape: there is NO top-level listing_status
 *  — only `marketplaces[].listing_status` per channel. A group row (uid ==
 *  null) is never hidden: restock_hub.py copies the FIRST member's
 *  breakdown, so a group's status is one member's, not the group's, and
 *  hiding on it would be wrong. An ungrouped row stays if it is Active on at
 *  least one marketplace. */
export function isActiveSrRow(r: {
  uid: string | null
  marketplaces: Array<{ listing_status?: ListingStatus | null }>
}): boolean {
  if (r.uid == null) return true
  return r.marketplaces.some((m) => m.listing_status === 'active')
}

/** `on=false` returns the SAME array reference (and hidden:0) so a memoised
 *  caller stays stable when the filter is off. */
export function applyActiveOnly<T>(
  rows: T[],
  on: boolean,
  pred: (r: T) => boolean,
): { rows: T[]; hidden: number } {
  if (!on) return { rows, hidden: 0 }
  const kept = rows.filter(pred)
  return { rows: kept, hidden: rows.length - kept.length }
}

export function hiddenLabel(n: number): string {
  return `hiding ${n} not active (inactive, in review, or no listing found)`
}
